import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    List,
    ListItem,
    CircularProgress,
    Chip,
    InputAdornment,
    Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventIcon from '@mui/icons-material/Event';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import dayjs from 'dayjs';

const CompetitionSearchModal = ({ open, onClose, onSelect, excludeIds = [] }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [recommendations, setRecommendations] = useState([]);
    const [loadingRecommendations, setLoadingRecommendations] = useState(false);

    // Check if competition is within 4-month exclusion window
    const isWithinExclusion = (usedAt) => {
        if (!usedAt) return false;
        const fourMonthsAgo = new Date();
        fourMonthsAgo.setMonth(fourMonthsAgo.getMonth() - 4);
        return new Date(usedAt) > fourMonthsAgo;
    };

    // Calculate days until reusable
    const getDaysUntilReusable = (usedAt) => {
        if (!usedAt) return null;
        const usedDate = new Date(usedAt);
        const reusableDate = new Date(usedDate);
        reusableDate.setMonth(reusableDate.getMonth() + 4);
        const now = new Date();
        const msRemaining = reusableDate.getTime() - now.getTime();
        if (msRemaining <= 0) return null;
        return Math.ceil(msRemaining / (24 * 60 * 60 * 1000));
    };

    const searchCompetitions = useCallback(async (query) => {
        if (!query || query.trim().length === 0) {
            setResults([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const excludeParam = excludeIds.length > 0 ? `&exclude=${excludeIds.join(',')}` : '';
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/competitions/search?q=${encodeURIComponent(query)}${excludeParam}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setResults(response.data.competitions || []);
        } catch (err) {
            console.error('Failed to search competitions:', err);
            setError(err.response?.data?.error || 'Failed to search competitions');
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, [excludeIds]);

    // Fetch recommendations on modal open
    const fetchRecommendations = useCallback(async () => {
        setLoadingRecommendations(true);
        try {
            const excludeParam = excludeIds.length > 0 ? `?exclude=${excludeIds.join(',')}` : '';
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/competitions/recommended${excludeParam}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setRecommendations(response.data.competitions || []);
        } catch (err) {
            console.error('Failed to fetch recommendations:', err);
            setRecommendations([]);
        } finally {
            setLoadingRecommendations(false);
        }
    }, [excludeIds]);

    useEffect(() => {
        if (open) {
            fetchRecommendations();
        }
    }, [open, fetchRecommendations]);

    // Debounced search
    useEffect(() => {
        if (searchQuery.trim().length === 0) {
            setResults([]);
            return;
        }

        const timer = setTimeout(() => {
            searchCompetitions(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery, searchCompetitions]);

    // Clear state when modal closes
    useEffect(() => {
        if (!open) {
            setSearchQuery('');
            setResults([]);
            setError(null);
            setRecommendations([]);
        }
    }, [open]);

    const handleSelect = (competition, useAI = false) => {
        onSelect(competition, useAI);
        onClose();
    };

    const getSubjectLabel = (subject) => {
        if (!subject || !Array.isArray(subject) || subject.length === 0) {
            return null;
        }
        return subject.join(', ');
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'competition':
                return 'Competition';
            case 'summer_program':
                return 'Summer Program';
            case 'enrichment_program':
                return 'Enrichment';
            default:
                return type || 'Competition';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SwapHorizIcon />
                    Replace Competition
                </Box>
            </DialogTitle>

            <DialogContent sx={{ minHeight: 560 }}>
                <TextField
                    fullWidth
                    placeholder="Search competitions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: loading && (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ),
                    }}
                />

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {!loading && searchQuery.trim().length > 0 && results.length === 0 && !error && (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                            No competitions found matching "{searchQuery}"
                        </Typography>
                    </Box>
                )}

                {results.length > 0 && (
                    <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                        {results.map((comp, index) => {
                            const excluded = isWithinExclusion(comp.used_in_published_newsletter_at);
                            const daysRemaining = getDaysUntilReusable(comp.used_in_published_newsletter_at);
                            const subjectLabel = getSubjectLabel(comp.subject);

                            return (
                                <React.Fragment key={comp.id}>
                                    <ListItem
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            py: 1.5,
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box sx={{ flex: 1, mr: 2 }}>
                                                <Typography variant="subtitle1" fontWeight="medium">
                                                    {comp.name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                                    {subjectLabel && (
                                                        <Chip
                                                            label={subjectLabel}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    )}
                                                    <Chip
                                                        label={getTypeLabel(comp.competition_type)}
                                                        size="small"
                                                        color="default"
                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                    {excluded ? (
                                                        <Chip
                                                            icon={<BlockIcon sx={{ fontSize: '0.9rem !important' }} />}
                                                            label={`${daysRemaining}d`}
                                                            size="small"
                                                            color="warning"
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<CheckCircleOutlineIcon sx={{ fontSize: '0.9rem !important' }} />}
                                                            label={comp.used_in_published_newsletter_at ? 'Available' : 'New'}
                                                            size="small"
                                                            color="success"
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleSelect(comp, false)}
                                                >
                                                    Select
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<AutoAwesomeIcon />}
                                                    onClick={() => handleSelect(comp, true)}
                                                >
                                                    + AI
                                                </Button>
                                            </Box>
                                        </Box>
                                    </ListItem>
                                    {index < results.length - 1 && <Divider />}
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}

                {searchQuery.trim().length === 0 && (
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EventIcon fontSize="small" />
                            Recommended (events in next 1-4 months)
                        </Typography>

                        {loadingRecommendations ? (
                            <Box sx={{ py: 2, textAlign: 'center' }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : recommendations.length > 0 ? (
                            <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                                {recommendations.map((comp, index) => {
                                    const subjectLabel = getSubjectLabel(comp.subject);

                                    return (
                                        <React.Fragment key={comp.id}>
                                            <ListItem
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    py: 1.5,
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover',
                                                    },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <Box sx={{ flex: 1, mr: 2 }}>
                                                        <Typography variant="subtitle1" fontWeight="medium">
                                                            {comp.name}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                                            {subjectLabel && (
                                                                <Chip
                                                                    label={subjectLabel}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                                />
                                                            )}
                                                            {comp.calendar_events?.map((event, eventIdx) => (
                                                                <Chip
                                                                    key={eventIdx}
                                                                    icon={<EventIcon sx={{ fontSize: '0.9rem !important' }} />}
                                                                    label={`${event.name}: ${dayjs(event.date).format('MMM D')}`}
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleSelect(comp, false)}
                                                        >
                                                            Select
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<AutoAwesomeIcon />}
                                                            onClick={() => handleSelect(comp, true)}
                                                        >
                                                            + AI
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </ListItem>
                                            {index < recommendations.length - 1 && <Divider />}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        ) : (
                            <Box sx={{ py: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    No recommendations available. Try searching above.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CompetitionSearchModal;
